import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { Workflow } from "../workflow.types";
import type { WorkflowCanvasEdge, WorkflowCanvasElement } from "./workflow-canvas.types";
import { getDefaultWorkflowCanvasElements } from "./useWorkflowCanvasElements";
import { definitionToCanvas } from "./workflow-graph-converter";

/**
 * Synchronizes workflow definition data into canvas local state.
 *
 * - Clears elements/edges immediately when `id` changes.
 * - Populates canvas once when the matching workflow data arrives.
 * - Uses `definitionToCanvas` to convert server graph format.
 * - Returns a sync counter (useful for triggering fitToContent)
 *   and a `resync` function to force re-synchronization.
 */
export const useWorkflowCanvasSync = (
	id: string,
	workflow: Workflow | undefined,
	isWorkflowLoaded: boolean,
	setElements: Dispatch<SetStateAction<WorkflowCanvasElement[]>>,
	setEdges: Dispatch<SetStateAction<WorkflowCanvasEdge[]>>,
): { syncCount: number; resync: () => void } => {
	const syncedIdRef = useRef<string | null>(null);
	const [syncCount, setSyncCount] = useState(0);
	const [resyncTrigger, setResyncTrigger] = useState(0);

	const resync = useCallback(() => {
		syncedIdRef.current = null;
		setResyncTrigger((c) => c + 1);
	}, []);

	// Reset canvas immediately when the active workflow id changes.
	useEffect(() => {
		syncedIdRef.current = null;
		setElements([]);
		setEdges([]);
	}, [id, setElements, setEdges]);

	// Populate canvas once when data for the current id is available.
	useEffect(() => {
		if (syncedIdRef.current === id) return;
		if (!isWorkflowLoaded || !workflow || workflow.id !== id) return;

		syncedIdRef.current = id;

		if (workflow.definition) {
			const result = definitionToCanvas(workflow.definition);
			if (result) {
				setElements(result.elements);
				setEdges(result.edges);
				setSyncCount((c) => c + 1);
				return;
			}
		}

		setElements(getDefaultWorkflowCanvasElements());
	}, [id, workflow, isWorkflowLoaded, setElements, setEdges, resyncTrigger]);

	return { syncCount, resync };
};
